import { useState } from "react"

import useHasAccess from "@hooks/useHasAccess"

import { NO_REFERENCE_SENTINEL } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"
import isModKeyPressed from "@utils/isModKeyPressed"
import replaceSlugs from "@utils/replaceSlugs"

import { Avatar, IconButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import { Prompt } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import {
    useSubscribeMutation,
    useSubscriptionPreviewCountMutation,
    useUnsubscribeMutation,
} from "@pages/Settings/SettingsCatalogs/SettingsCatalogs.store"

import { CATALOG_ROUTES } from "@routes/catalogs"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./CatalogBrandRow.module.scss"
import { CatalogBrandRowProps } from "./CatalogBrandRow.types"

function getSubscriptionCaption(subscribedItemCount: number, itemCount: number): string {
    const itemNoun = itemCount === 1 ? "item" : "items"

    if (subscribedItemCount === 0) {
        return `${itemCount} ${itemNoun}`
    }

    return `${subscribedItemCount} of ${itemCount} ${itemNoun} subscribed`
}

export default function CatalogBrandRow(props: CatalogBrandRowProps) {
    const { catalogId, brand, count, brandSubscription, isFullySubscribed } = props

    const { hasPermission } = useHasAccess()
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const subscribedItemCount = count?.subscribed_item_count ?? 0
    const itemCount = count?.item_count ?? 0

    const previewCountMutation = useSubscriptionPreviewCountMutation()
    const subscribeMutation = useSubscribeMutation()
    const unsubscribeMutation = useUnsubscribeMutation()

    const [isSubscribePromptOpen, setIsSubscribePromptOpen] = useState(false)
    const [isUnsubscribePromptOpen, setIsUnsubscribePromptOpen] = useState(false)

    const isNoBrand = brand === null
    const displayName = brand === null ? "(No brand)" : brand.name

    const brandItemsRoute = replaceSlugs(CATALOG_ROUTES.BRAND_ITEMS, {
        catalog_id: String(catalogId),
        brand_id: String(brand?.id ?? NO_REFERENCE_SENTINEL),
    })

    const handleRowClick = () => {
        if (isModKeyPressed()) {
            window.open(brandItemsRoute, "_blank", "noopener,noreferrer")
        } else {
            window.location.assign(brandItemsRoute)
        }
    }

    // A null brand omits the brand key, subscribing to the catalog's "No Brand" bucket.
    const subscribePayload = { catalog: catalogId, level: "brand" as const, brand: brand?.id }

    const handleSubscribeClick = () => {
        setIsSubscribePromptOpen(true)
        previewCountMutation.mutate(subscribePayload)
    }

    const handleSubscribeConfirm = () => {
        setIsSubscribePromptOpen(false)
        subscribeMutation.mutate(subscribePayload)
    }

    const handleUnsubscribeConfirm = () => {
        setIsUnsubscribePromptOpen(false)
        if (brandSubscription !== null) {
            unsubscribeMutation.mutate(brandSubscription)
        }
    }

    const isActionPending = subscribeMutation.isPending || unsubscribeMutation.isPending

    // Unsubscribing is offered only when the brand has its own subscription row: coverage that comes
    // from the whole-catalog subscription or from item-level rows has nothing at this level to remove.
    const menuItems: MenuContentNode[] = [
        {
            type: "group",
            items: [
                {
                    label: "View items",
                    icon: faIcon({ name: "arrow-up-right-from-square", style: "regular" }),
                    path: brandItemsRoute,
                },
                ...(hasPricebookEditPermission && !isFullySubscribed
                    ? [
                          {
                              label: isNoBrand ? "Subscribe" : "Subscribe to brand",
                              icon: faIcon({ name: "circle-plus", style: "regular" as const }),
                              onClick: handleSubscribeClick,
                          },
                      ]
                    : []),
            ],
        },
        ...(hasPricebookEditPermission && brandSubscription !== null
            ? [
                  { type: "divider" as const },
                  {
                      type: "group" as const,
                      items: [
                          {
                              variant: "danger" as const,
                              label: isNoBrand ? "Unsubscribe" : "Unsubscribe from brand",
                              icon: faIcon({ name: "ban", style: "regular" as const }),
                              onClick: () => setIsUnsubscribePromptOpen(true),
                          },
                      ],
                  },
              ]
            : []),
    ]

    const scopeLabel = isNoBrand ? "items with no brand" : displayName
    const newItemsScope = isNoBrand ? "without a brand" : "to this brand"

    const previewCount = previewCountMutation.data?.count
    const importedItems =
        previewCount === undefined ? "the covered items" : `${previewCount} ${previewCount === 1 ? "item" : "items"}`
    const subscribePromptBody = `This will import ${importedItems} into your pricebook and keep them in sync. New items added ${newItemsScope} later will be imported automatically.`
    const unsubscribePromptBody = `Items already in your pricebook will stay there but will no longer receive catalog updates. New items added ${newItemsScope} will no longer be imported automatically.`

    // The wrapper shields the menu from the row-level click handler, so opening the
    // menu (or picking one of its items) never doubles as a row navigation.
    const stopRowClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        event.stopPropagation()
    }

    return (
        <>
            <ListItem.Root isClickable={true} onClick={handleRowClick} aria-label={`View ${displayName} items`}>
                <ListItem.AvatarAndContent
                    avatar={
                        brand === null ? (
                            // Reserve the avatar slot so the label column lines up with sibling rows.
                            <div className={styles.avatarSpacer} aria-hidden={true} />
                        ) : (
                            <Avatar
                                variant="company"
                                size="lg"
                                src={brand.image_url || undefined}
                                fallbackName={brand.name}
                            />
                        )
                    }
                    content={
                        <ListItemContent
                            leftLabel={
                                <ListItem.Label
                                    label={displayName}
                                    suffixSlot={
                                        isFullySubscribed ? (
                                            <AlertBadge status="success" size="sm" showIcon={true} isLoading={false}>
                                                Subscribed
                                            </AlertBadge>
                                        ) : undefined
                                    }
                                />
                            }
                            leftCaption={
                                <ListItem.Caption caption={getSubscriptionCaption(subscribedItemCount, itemCount)} />
                            }
                        />
                    }
                />
                <ListItem.Actions>
                    <div
                        className={styles.menuGuard}
                        onClick={stopRowClick}
                        onKeyDown={stopRowClick}
                        role="presentation"
                    >
                        <Menu
                            popoverSide="bottom"
                            popoverAlign="end"
                            title={displayName}
                            trigger={
                                <IconButton
                                    variant="ghost"
                                    size="md"
                                    icon={faIcon({ name: "ellipsis", style: "regular" })}
                                    aria-label="More options"
                                    isProcessing={isActionPending}
                                    isDisabled={isActionPending}
                                />
                            }
                            items={menuItems}
                        />
                    </div>
                </ListItem.Actions>
            </ListItem.Root>

            <Prompt
                title={`Subscribe to ${scopeLabel}?`}
                body={subscribePromptBody}
                isOpen={isSubscribePromptOpen}
                isLoading={previewCountMutation.isPending}
                confirmText="Subscribe"
                onConfirm={handleSubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsSubscribePromptOpen(false)}
            />

            <Prompt
                title={`Unsubscribe from ${scopeLabel}?`}
                body={unsubscribePromptBody}
                isOpen={isUnsubscribePromptOpen}
                confirmText="Unsubscribe"
                onConfirm={handleUnsubscribeConfirm}
                cancelText="Cancel"
                onCancel={() => setIsUnsubscribePromptOpen(false)}
            />
        </>
    )
}
